import React, { useState, useEffect } from 'react';
import { 
  ChevronRightIcon, 
  ChevronDownIcon, 
  BuildingOfficeIcon, 
  MapPinIcon, 
  BookOpenIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { University, Campus, Course } from '../types/UniversityPortal';
import { universityPortalApi } from '../services/universityPortalApi';

interface UniversityTreeViewProps {
  universities: University[];
  onEdit: (type: 'university' | 'campus' | 'course', item: any) => void;
  onDelete: (type: 'university' | 'campus' | 'course', item: any) => void;
  onView: (type: 'university' | 'campus' | 'course', item: any) => void;
  loadingOperations: Set<string>;
  isOperationLoading: (operationId: string) => boolean;
}

interface ExpandedState {
  [key: string]: boolean;
}

const UniversityTreeView: React.FC<UniversityTreeViewProps> = ({
  universities,
  onEdit,
  onDelete,
  onView,
  loadingOperations,
  isOperationLoading
}) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [universityData, setUniversityData] = useState<{
    [universityId: number]: {
      campuses: Campus[];
      courses: Course[];
      loading: boolean;
    }
  }>({});

  const toggleExpanded = (id: string) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const loadUniversityData = async (universityId: number) => {
    if (universityData[universityId]) return; // Already loaded

    setUniversityData(prev => ({
      ...prev,
      [universityId]: { campuses: [], courses: [], loading: true }
    }));

    try {
      const [campuses, courses] = await Promise.all([
        universityPortalApi.getUniversityCampuses(universityId),
        universityPortalApi.getUniversityCourses(universityId)
      ]);

      setUniversityData(prev => ({
        ...prev,
        [universityId]: { campuses, courses, loading: false }
      }));
    } catch (error) {
      console.error('Failed to load university data:', error);
      setUniversityData(prev => ({
        ...prev,
        [universityId]: { campuses: [], courses: [], loading: false }
      }));
    }
  };

  const handleUniversityClick = (university: University) => {
    const universityId = `university-${university.id}`;
    toggleExpanded(universityId);
    loadUniversityData(university.id);
  };

  const handleCampusClick = (campus: Campus) => {
    const campusId = `campus-${campus.id}`;
    toggleExpanded(campusId);
  };

  const renderUniversity = (university: University) => {
    const universityId = `university-${university.id}`;
    const isExpanded = expanded[universityId];
    const data = universityData[university.id];
    const isLoading = data?.loading || false;

    return (
      <div key={university.id} className="border border-gray-200 rounded-lg mb-2">
        <div 
          className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
          onClick={() => handleUniversityClick(university)}
        >
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {isExpanded ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
            <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">{university.name}</h3>
              <p className="text-xs text-gray-500">
                {university.description || 'No description'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              university.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {university.isActive ? 'Active' : 'Inactive'}
            </span>
            
            <div className="flex space-x-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView('university', university);
                }}
                className="p-1 text-green-600 hover:text-green-900"
                title="View"
              >
                <EyeIcon className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit('university', university);
                }}
                className="p-1 text-blue-600 hover:text-blue-900"
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete('university', university);
                }}
                disabled={isOperationLoading(`delete-university-${university.id}`)}
                className="p-1 text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete"
              >
                {isOperationLoading(`delete-university-${university.id}`) ? (
                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <TrashIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 bg-gray-50">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {/* Campuses */}
                {data?.campuses && data.campuses.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">
                      Campuses ({data.campuses.length})
                    </h4>
                    <div className="space-y-1">
                      {data.campuses.map(campus => renderCampus(campus))}
                    </div>
                  </div>
                )}

                {/* Courses */}
                {data?.courses && data.courses.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">
                      Courses ({data.courses.length})
                    </h4>
                    <div className="space-y-1">
                      {data.courses.map(course => renderCourse(course))}
                    </div>
                  </div>
                )}

                {(!data?.campuses?.length && !data?.courses?.length) && (
                  <div className="text-center text-gray-500 py-4">
                    <p className="text-sm">No campuses or courses found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCampus = (campus: Campus) => {
    const campusId = `campus-${campus.id}`;
    const isExpanded = expanded[campusId];

    return (
      <div key={campus.id} className="bg-white border border-gray-200 rounded-md">
        <div 
          className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
          onClick={() => handleCampusClick(campus)}
        >
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              )}
            </div>
            <MapPinIcon className="h-4 w-4 text-green-600" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">{campus.name}</h4>
              <p className="text-xs text-gray-500">
                {campus.city}, {campus.country}
                {campus.isMainCampus && (
                  <span className="ml-2 inline-flex px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    Main Campus
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              campus.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {campus.isActive ? 'Active' : 'Inactive'}
            </span>
            
            <div className="flex space-x-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView('campus', campus);
                }}
                className="p-1 text-green-600 hover:text-green-900"
                title="View"
              >
                <EyeIcon className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit('campus', campus);
                }}
                className="p-1 text-blue-600 hover:text-blue-900"
                title="Edit"
              >
                <PencilIcon className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete('campus', campus);
                }}
                disabled={isOperationLoading(`delete-campus-${campus.id}`)}
                className="p-1 text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete"
              >
                {isOperationLoading(`delete-campus-${campus.id}`) ? (
                  <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <TrashIcon className="h-3 w-3" />
                )}
              </button>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 bg-gray-50 p-3">
            <div className="text-xs text-gray-600">
              <p><strong>Address:</strong> {campus.address || 'Not specified'}</p>
              {campus.phone && <p><strong>Phone:</strong> {campus.phone}</p>}
              {campus.email && <p><strong>Email:</strong> {campus.email}</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCourse = (course: Course) => {
    return (
      <div key={course.id} className="bg-white border border-gray-200 rounded-md">
        <div className="flex items-center justify-between p-3 hover:bg-gray-50">
          <div className="flex items-center space-x-3">
            <BookOpenIcon className="h-4 w-4 text-purple-600" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">{course.name}</h4>
              <p className="text-xs text-gray-500">
                {course.code && `Code: ${course.code}`}
                {course.credits && ` • ${course.credits} credits`}
                {course.durationMonths && ` • ${course.durationMonths} months`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              course.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {course.isActive ? 'Active' : 'Inactive'}
            </span>
            
            <div className="flex space-x-1">
              <button
                onClick={() => onView('course', course)}
                className="p-1 text-green-600 hover:text-green-900"
                title="View"
              >
                <EyeIcon className="h-3 w-3" />
              </button>
              <button
                onClick={() => onEdit('course', course)}
                className="p-1 text-blue-600 hover:text-blue-900"
                title="Edit"
              >
                <PencilIcon className="h-3 w-3" />
              </button>
              <button
                onClick={() => onDelete('course', course)}
                disabled={isOperationLoading(`delete-course-${course.id}`)}
                className="p-1 text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete"
              >
                {isOperationLoading(`delete-course-${course.id}`) ? (
                  <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <TrashIcon className="h-3 w-3" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (universities.length === 0) {
    return (
      <div className="text-center py-8">
        <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No universities found</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {universities.map(university => renderUniversity(university))}
    </div>
  );
};

export default UniversityTreeView;
